import { message } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";
import { initialMappingForm } from "../../const";

export const useChannelManager = () => {
  const [rooms, setRooms] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [channels, setChannels] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [formData, setFormData] = useState(initialMappingForm);

  useEffect(() => {
    fetchRooms();
    fetchRatePlans();
    fetchMappings();
    fetchSyncLogs();
    fetchChannels();
  }, []);

  const fetchChannels = async () => {
    try {
      const res = await axios.get("/api/channelmanager/channels");
      setChannels(res.data);
    } catch (error) {
      message.error("Failed to fetch channel status");
    }
  };

  const fetchRooms = async () => {
    try {
      const res = await axios.get("/api/rooms/getallrooms");
      setRooms(res.data);
    } catch (error) {
      message.error("Failed to fetch rooms");
    }
  };

  const fetchRatePlans = async () => {
    try {
      const res = await axios.get("/api/rateplans/getallrateplans");
      setRatePlans(res.data);
    } catch (error) {
      message.error("Failed to fetch rate plans");
    }
  };

  const fetchMappings = async () => {
    try {
      const res = await axios.get("/api/channelmanager/mappings");
      setMappings(res.data);
    } catch (error) {
      message.error("Failed to fetch SiteMinder mappings");
    }
  };

  const fetchSyncLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await axios.get("/api/channelmanager/synclogs?limit=50");
      setSyncLogs(res.data);
    } catch (error) {
      message.error("Failed to fetch sync log");
    } finally {
      setLogsLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };

      // Auto-fill display names/occupancy when room or rate plan changes.
      if (field === "room") {
        const room = rooms.find((r) => r._id === value);
        if (room) next.roomTypeName = next.roomTypeName || room.name;
      }
      if (field === "ratePlan") {
        const ratePlan = ratePlans.find((r) => r._id === value);
        if (ratePlan) {
          next.ratePlanName = next.ratePlanName || ratePlan.rateplanname;
          next.rateSetupType = ratePlan.ratePlanSetup || next.rateSetupType;
        }
      }
      return next;
    });
  };

  const handleCreate = () => {
    setEditingMapping(null);
    setFormData(initialMappingForm);
    setIsModalVisible(true);
  };

  const handleEdit = (mapping) => {
    setEditingMapping(mapping);
    setFormData({
      channel: mapping.channel,
      room: mapping.room?._id || mapping.room,
      ratePlan: mapping.ratePlan?._id || mapping.ratePlan,
      roomTypeCode: mapping.roomTypeCode,
      ratePlanCode: mapping.ratePlanCode,
      roomTypeName: mapping.roomTypeName,
      ratePlanName: mapping.ratePlanName,
      includedAdultOccupancy: mapping.includedAdultOccupancy,
      rateSetupType: mapping.rateSetupType,
    });
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      if (editingMapping) {
        await axios.put(`/api/channelmanager/mappings/${editingMapping._id}`, formData);
        message.success("Mapping updated");
      } else {
        await axios.post("/api/channelmanager/mappings", formData);
        message.success("Mapping created");
      }
      setIsModalVisible(false);
      fetchMappings();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to save mapping");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/channelmanager/mappings/${id}`);
      message.success("Mapping deleted");
      fetchMappings();
    } catch (error) {
      message.error("Failed to delete mapping");
    }
  };

  return {
    rooms,
    ratePlans,
    mappings,
    syncLogs,
    logsLoading,
    channels,
    isModalVisible,
    editingMapping,
    formData,
    setIsModalVisible,
    handleFieldChange,
    handleCreate,
    handleEdit,
    handleModalOk,
    handleDelete,
    fetchSyncLogs,
    fetchChannels,
  };
};
